import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import "./PaginatedList.css";

export function PaginatedList({
    items,
    title = "Événements à venir",
    subtitle = "Ne manquez pas ces événements !",
    pageSize = 3,
    dateFilter = ev =>
        dayjs(ev?.startDate).isAfter(dayjs()) ||
        dayjs(ev?.startDate).isSame(dayjs(), "day"),
    sortFn = (a, b) =>
        dayjs(a?.startDate).diff(dayjs(b?.startDate)),
    renderEvent,
}) {
    const filteredSorted = useMemo(() => {
        return (items || [])
            .filter(dateFilter)
            .sort(sortFn);
    }, [items, dateFilter, sortFn]);

    const [currentPage, setCurrentPage] = useState(1);
    const pageCount = Math.ceil(filteredSorted?.length / pageSize);
    const pageData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredSorted?.slice(start, start + pageSize);
    }, [filteredSorted, currentPage, pageSize]);

    const getVisiblePages = () => {
        const maxVisible = 6;
        if (pageCount <= maxVisible) {
            return Array.from({ length: pageCount }, (_, i) => i + 1);
        }
        const half = Math.floor(maxVisible / 2);
        let start = currentPage - half;
        let end = currentPage + half;
        if (start < 1) {
            start = 1;
            end = maxVisible;
        } else if (end > pageCount) {
            end = pageCount;
            start = pageCount - maxVisible + 1;
        }
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };


    const defaultRender = (ev, idx) => (
        <li className="pl-event-item" key={idx}>
            <a className="pl-event-link" href={ev.youtubeLink}>
                {ev.title}
                {ev.place && <span> — à {ev.place}</span>}
            </a>
            <ul className="pl-event-meta">
                <li>Date : {dayjs(ev.startDate).format("DD MMMM YYYY")}</li>
                {ev.category && <li>Catégorie : {ev.category}</li>}
            </ul>
        </li>
    );
    const titleStyle = { fontSize: '20px', fontWeight: 'bold', textAlign: "center" };
    const subtitleStyle = { fontSize: '14px', textAlign: "center" };

    return (
        <div className="pl-container">
            <div className="pl-container-items">
                <h5 style={titleStyle}>
                    {title}
                </h5>
                <p style={subtitleStyle}>
                    {subtitle}
                </p>

                <ul className="pl-list">
                    {pageData?.map((ev, i) =>
                        (renderEvent || defaultRender)(ev, i)
                    )}
                </ul>
            </div>

            {pageCount > 1 && (
                <div className="pl-pagination">
                    {/* Flèche Précédent */}
                    <button
                        className="pl-arrow"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Page précédente"
                    >
                        ‹
                    </button>

                    {/* Points visibles */}
                    {getVisiblePages()?.map(page => (
                        <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={
                                page === currentPage
                                    ? "pl-dot pl-dot--active"
                                    : "pl-dot"
                            }
                            aria-label={`Page ${page}`}
                        />
                    ))}

                    {/* Flèche Suivant */}
                    <button
                        className="pl-arrow"
                        onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))}
                        disabled={currentPage === pageCount}
                        aria-label="Page suivante"
                    >
                        ›
                    </button>
                </div>
            )}
        </div>
    );
}
